// The 12-lead on paper, in the answer area and enlarged. The card shows the whole sheet at the width of the
// screen; when enlargeable (the phone's answer area), a tap opens it full screen, turned a quarter when the phone is
// upright so the sheet uses the long side, with a zoom that can be panned and the seconds left so nobody loses track
// of the question. Elsewhere (the stage) it is a still sheet with no tap affordance.
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { Maximize2, X, ZoomIn, ZoomOut } from 'lucide-react';
import type { EcgGraphic } from '@shared/types';
import type { EcgParams } from './types';
import { ecgKey, ecgParams } from './params';
import { drawPaper, headerLines, PAPER_FONT, PAPER_WIDTH, paperBox, paperCells, paperHeight } from './paper';
import { measure, spokenMeasurements } from './measure';
import { LeadButtons, type LeadPick } from './LeadButtons';
import { canvasRatio, context2d, cssToken, effectiveRatio, MAX_SIDE, pixelRatio, watchEffectiveRatio, watchWidth } from '../env';
import { serverNow } from '@/lib/api';
import '../clinical.css';

/** The synthesiser's parameters for the sheet: the graphic's rhythm, rate, ST shifts and drawable measurements (params.ts). */
export const paperParams = (g: EcgGraphic): EcgParams => ecgParams(g);

export const paperColours = () => ({ paper: cssToken('--paper', '#FFFDF7'), ink: cssToken('--paper-ink', '#1F2733') });
const colours = paperColours;

/**
 * The sheet's layout width (CSS pixels before any ancestor's transform) and the effective pixel ratio it is shown at (the
 * screen's density times the stage's or any other ancestor's scale), measured before the first paint and kept current.
 */
export function useSheetSize(sizer: { current: HTMLElement | null }): { width: number; ratio: number } {
  const [width, setWidth] = useState(0);
  const [ratio, setRatio] = useState(1);
  useLayoutEffect(() => {
    const el = sizer.current;
    if (!el) return;
    setRatio(effectiveRatio(el));
    const offWidth = watchWidth(el, setWidth);
    const offRatio = watchEffectiveRatio(el, setRatio);
    return () => { offWidth(); offRatio(); };
  }, [sizer]);
  return { width, ratio };
}

/** Paint once now and again when the machine font has loaded. */
export function usePaint(paint: () => void, deps: unknown[]) {
  useEffect(() => {
    let alive = true;
    paint();
    const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
    if (fonts?.load) fonts.load(PAPER_FONT).then(() => { if (alive) paint(); }, () => undefined);
    return () => { alive = false; };
    // the caller lists what the paint depends on
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

/** What the sheet says aloud: the layout, the paper settings and the machine's measurements, never an interpretation. */
const sheetLabel = (title: string, params: EcgParams) =>
  `${title}: twelve leads and a lead II rhythm strip, 25 millimetres per second, 10 millimetres per millivolt. ${spokenMeasurements(measure(params))}`;

/**
 * `pick` (still sheet only) marks the leads as regions: buttons a player can tap on a sheet big enough for it, or, with
 * `decorative`, quiet marks for the reveal on a small or broadcast sheet (right leads ringed in green, a wrong pick in red).
 */
export function EcgPaperCard({ graphic, deadlineAt, enlargeable = false, pick, revealed = false, decorative = false }: {
  graphic: EcgGraphic; deadlineAt?: number; enlargeable?: boolean; pick?: LeadPick; revealed?: boolean; decorative?: boolean;
}) {
  const key = ecgKey(graphic);
  // key stands for everything the sheet is drawn from
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const params = useMemo(() => paperParams(graphic), [key]);
  const lines = useMemo(() => headerLines(params), [params]);
  const sizer = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const { width, ratio } = useSheetSize(sizer);
  const [open, setOpen] = useState(false);
  // Laid out in layout pixels; the header type grows on a small sheet, so its height follows the scale.
  const scale = width > 0 ? width / PAPER_WIDTH : undefined;
  const sheetH = paperHeight(lines.length, scale);
  usePaint(() => {
    const cv = canvas.current, ctx = context2d(cv);
    if (!cv || !ctx || !scale) return;
    const css = paperBox(lines.length, scale);
    drawPaper(cv, ctx, { params, lines, scale, dpr: canvasRatio(ratio, css.width, css.height), colours: colours() });
  }, [params, lines, scale, ratio]);
  const close = useCallback(() => { setOpen(false); requestAnimationFrame(() => trigger.current?.focus()); }, []);
  const title = graphic.caption && graphic.caption.trim() ? graphic.caption : '12-lead ECG';
  const aspectRatio = `${PAPER_WIDTH} / ${sheetH}`;
  if (!enlargeable) {
    const tappable = Boolean(pick) && !decorative;
    return (
      <figure className="rx-paper-card" data-still="" data-revealed={revealed ? '' : undefined} role={tappable ? 'group' : 'img'} aria-label={sheetLabel(title, params)}>
        <div ref={sizer} className="rx-paper-sizer" style={{ aspectRatio }}>
          <canvas ref={canvas} aria-hidden="true" />
          {pick && <LeadButtons cells={paperCells(lines.length, scale)} width={PAPER_WIDTH} height={sheetH} pick={pick} revealed={revealed} names={false} decorative={decorative} />}
        </div>
        <figcaption className="rx-paper-foot" aria-hidden="true"><span>{title} · 25 mm/s · 10 mm/mV</span></figcaption>
      </figure>
    );
  }
  return (
    <>
      <button ref={trigger} type="button" className="rx-paper-card" aria-haspopup="dialog" aria-label={`${title}. Tap to enlarge.`} onClick={() => setOpen(true)}>
        <div ref={sizer} className="rx-paper-sizer" style={{ aspectRatio }}>
          <canvas ref={canvas} aria-hidden="true" />
        </div>
        <span className="rx-paper-foot" aria-hidden="true">
          <span>{title} · 25 mm/s · 10 mm/mV</span>
          <span className="rx-paper-hint"><Maximize2 size={13} strokeWidth={2.4} />Tap to enlarge</span>
        </span>
      </button>
      <AnimatePresence>
        {open && <PaperDialog key="dialog" params={params} lines={lines} title={title} deadlineAt={deadlineAt} onClose={close} />}
      </AnimatePresence>
    </>
  );
}

function PaperDialog({ params, lines, title, deadlineAt, onClose }: { params: EcgParams; lines: string[]; title: string; deadlineAt?: number; onClose: () => void }) {
  const reduce = useReducedMotion() ?? false;
  const [box, setBox] = useState(() => ({ w: window.innerWidth - 20, h: window.innerHeight - 90 }));
  const [zoom, setZoom] = useState(false);
  const canvas = useRef<HTMLCanvasElement>(null);
  const stage = useRef<HTMLDivElement>(null);
  const closeBtn = useRef<HTMLButtonElement>(null);
  const focusPoint = useRef<{ fx: number; fy: number } | null>(null);

  // Fit to the space the stage really has, whatever the bar and the safe areas take.
  useLayoutEffect(() => {
    const el = stage.current;
    if (!el) return;
    const measure = () => {
      const cs = getComputedStyle(el);
      const w = el.clientWidth - parseFloat(cs.paddingLeft || '0') - parseFloat(cs.paddingRight || '0');
      const h = el.clientHeight - parseFloat(cs.paddingTop || '0') - parseFloat(cs.paddingBottom || '0');
      if (w > 0 && h > 0) setBox((b) => (Math.abs(b.w - w) < 1 && Math.abs(b.h - h) < 1 ? b : { w, h }));
    };
    measure();
    if (typeof ResizeObserver === 'function') { const ro = new ResizeObserver(measure); ro.observe(el); return () => ro.disconnect(); }
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);
  useEffect(() => {
    closeBtn.current?.focus();
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); onClose(); } };
    document.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = prev; document.removeEventListener('keydown', onKey); };
  }, [onClose]);

  const rotate = box.h > box.w * 1.1;
  const availW = Math.max(120, box.w), availH = Math.max(120, box.h);
  // The header type grows on a small sheet, which makes the sheet taller: fit, then fit again at the height it will have.
  const fitAt = (H: number) => (rotate ? Math.min(availW / H, availH / PAPER_WIDTH) : Math.min(availW / PAPER_WIDTH, availH / H));
  let fit = fitAt(paperHeight(lines.length));
  fit = fitAt(paperHeight(lines.length, fit));
  // Zoomed, the sheet may be wider than any canvas can be; past that the zoom stops, and below it the pixel ratio gives way
  // first (canvasRatio), so a 3× phone keeps the same zoom as a 2× one.
  const scale = Math.max(0.1, Math.min(zoom ? fit * 2.2 : fit, MAX_SIDE / Math.max(PAPER_WIDTH, paperHeight(lines.length))));
  const css = paperBox(lines.length, scale, rotate);
  // The dialog sits in its own layer over the page, with no scaled ancestor: the screen's density is the whole story.
  const dpr = canvasRatio(pixelRatio(), css.width, css.height);

  usePaint(() => {
    const cv = canvas.current, ctx = context2d(cv);
    if (!cv || !ctx) return;
    drawPaper(cv, ctx, { params, lines, scale, rotate, dpr, colours: colours() });
  }, [params, lines, scale, rotate, dpr]);

  // Keep the spot that was tapped under the finger when zooming in.
  useLayoutEffect(() => {
    const s = stage.current, cv = canvas.current, p = focusPoint.current;
    if (!s || !cv) return;
    if (zoom && p) {
      s.scrollLeft = Math.max(0, p.fx * cv.offsetWidth - s.clientWidth / 2);
      s.scrollTop = Math.max(0, p.fy * cv.offsetHeight - s.clientHeight / 2);
    } else { s.scrollLeft = 0; s.scrollTop = 0; }
    focusPoint.current = null;
  }, [zoom, scale]);

  const toggleAt = (e: ReactMouseEvent<HTMLCanvasElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    focusPoint.current = r.width > 0 ? { fx: (e.clientX - r.left) / r.width, fy: (e.clientY - r.top) / r.height } : null;
    setZoom((z) => !z);
  };

  return createPortal(
    <motion.div className="rx-paper-dialog" role="dialog" aria-modal="true" aria-label={`${title}, enlarged`}
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.18 }}>
      <div className="rx-paper-bar">
        <span className="rx-paper-title">{title}</span>
        {deadlineAt !== undefined && <SecondsLeft deadlineAt={deadlineAt} />}
        <button type="button" className="rx-paper-btn" aria-pressed={zoom} aria-label={zoom ? 'Fit the whole ECG' : 'Zoom in'} onClick={() => { focusPoint.current = { fx: 0.5, fy: 0.5 }; setZoom((z) => !z); }}>
          {zoom ? <ZoomOut size={18} strokeWidth={2.2} aria-hidden="true" /> : <ZoomIn size={18} strokeWidth={2.2} aria-hidden="true" />}
          <span className="rx-paper-btn-text" aria-hidden="true">{zoom ? 'Fit' : 'Zoom'}</span>
        </button>
        <button ref={closeBtn} type="button" className="rx-paper-btn" aria-label="Close" onClick={onClose}>
          <X size={18} strokeWidth={2.4} aria-hidden="true" />
          <span className="rx-paper-btn-text" aria-hidden="true">Close</span>
        </button>
      </div>
      <div className="rx-paper-stage" ref={stage} data-zoom={zoom ? '' : undefined}>
        <motion.canvas ref={canvas} role="img" aria-label={`${sheetLabel(title, params)}${rotate ? ' Turn your phone sideways to read it.' : ''}`}
          onClick={toggleAt} initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }}
          transition={reduce ? { duration: 0.15 } : { type: 'spring', stiffness: 300, damping: 28 }} />
      </div>
    </motion.div>,
    document.body,
  );
}

/** Seconds left on the question, so an enlarged ECG never hides the clock. */
export function SecondsLeft({ deadlineAt }: { deadlineAt: number }) {
  const [left, setLeft] = useState(() => Math.max(0, Math.ceil((deadlineAt - serverNow()) / 1000)));
  useEffect(() => {
    const id = window.setInterval(() => setLeft(Math.max(0, Math.ceil((deadlineAt - serverNow()) / 1000))), 250);
    return () => window.clearInterval(id);
  }, [deadlineAt]);
  return (
    <span className="rx-paper-time" data-low={left <= 5 ? '' : undefined} aria-live="off">
      {left > 0 ? `${left} s left` : "Time's up"}
    </span>
  );
}
